import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery, Tabs, Tab
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { AiFillBulb } from "react-icons/ai";

// ─── Styled Components (matching C++ series pattern) ──────────
const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(() => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

const TableHeader = ({ columns, cols }) => (
    <Box sx={{
        display: "grid",
        gridTemplateColumns: cols || `repeat(${columns.length},1fr)`,
        gap: "12px", padding: "12px 16px",
        bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod",
        fontWeight: "bold", fontSize: "0.78rem",
        letterSpacing: "0.06em", textTransform: "uppercase",
        borderBottom: "1px solid rgba(218,165,32,0.15)",
    }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

const SectionHeading = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 4, mb: subtitle ? 1 : 2 }}>
        <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38, flexShrink: 0, mt: 0.3 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{subtitle}</Typography>}
        </Box>
    </Box>
);

const ConceptCard = ({ title, color = "goldenrod", children }) => (
    <Box sx={{
        bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`, borderRadius: "12px",
        p: "16px 18px", mb: 2,
    }}>
        {title && <Typography sx={{ fontWeight: 700, color, fontSize: "0.82rem", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</Typography>}
        {children}
    </Box>
);

const StyledTabs = ({ value, onChange, tabs }) => (
    <Tabs value={value} onChange={onChange} variant="scrollable" scrollButtons="auto"
          sx={{
              mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)",
              "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 },
              "& .Mui-selected": { color: "goldenrod !important" },
              "& .MuiTabs-indicator": { bgcolor: "goldenrod" },
          }}>
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
    </Tabs>
);

const PillChip = ({ label, color = "goldenrod" }) => (
    <Chip label={label} size="small" sx={{
        bgcolor: `${color}18`, color, fontWeight: 700, fontSize: "0.72rem",
        height: 22, border: `1px solid ${color}44`, "& .MuiChip-label": { px: 1 },
    }} />
);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 1: C-Style Arrays
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const cArrayTabs = [
    {
        label: "Declaration & Init",
        code: `#include <iostream>

// ── Declaring C-style arrays ──────────────────────────────────
int    scores[5];                     // uninitialised — contains garbage!
int    scores2[5] = {0};              // all zeros — safest way to zero-init
int    primes[6]  = {2, 3, 5, 7, 11, 13};
double temps[]    = {98.6, 37.0, 100.4};  // size inferred from initialiser (3)
char   grade      = 'A';             // single char — NOT an array
char   grades[4]  = {'A','B','C','D'};    // array of chars

// ── Size is FIXED at compile time ─────────────────────────────
const int N = 10;
int data[N];           // ✅ OK — N is a compile-time constant
// int data[someVar]; // ❌ VLA — non-standard in C++ (avoid!)

// ── Accessing elements ────────────────────────────────────────
primes[0];   // 2  (first element — index 0)
primes[5];   // 13 (last element — index N-1)
// primes[6]; ← UNDEFINED BEHAVIOUR — no bounds check!

// ── Iterating ─────────────────────────────────────────────────
int n = sizeof(primes) / sizeof(primes[0]);  // get size: 6
for (int i = 0; i < n; ++i)
    std::cout << primes[i] << ' ';

// Range-for also works on C-arrays (compiler knows the size):
for (int x : primes)
    std::cout << x << ' ';`,
    },
    {
        label: "Arrays & Pointers",
        code: `#include <iostream>

// ── Arrays DECAY to pointers ──────────────────────────────────
// Array name = pointer to first element (in most contexts)
int arr[] = {10, 20, 30, 40, 50};
int* ptr  = arr;         // arr decays to &arr[0]

// Pointer arithmetic = array indexing
*ptr;         // 10  — same as arr[0]
*(ptr + 1);   // 20  — same as arr[1]
ptr[2];       // 30  — pointer indexing (same as arr[2])

// ── Passing arrays to functions ───────────────────────────────
// Arrays CANNOT be passed by value — always passed as pointer!
void printArray(int* arr, int size) {   // pointer + explicit size
    for (int i = 0; i < size; ++i)
        std::cout << arr[i] << ' ';
}
// Inside function: sizeof(arr) == sizeof(int*) — lost the size!

void printArray2(int arr[], int size) { // same as int* arr
    for (int i = 0; i < size; ++i)
        std::cout << arr[i] << ' ';
}

// ── Pointer arithmetic ────────────────────────────────────────
int* first = arr;
int* last  = arr + 4;          // points to arr[4] = 50
int  dist  = last - first;     // 4 — difference is element count, not bytes!

for (int* p = arr; p != arr + 5; ++p)
    std::cout << *p << ' ';    // pointer iteration over array`,
    },
    {
        label: "2D Arrays",
        code: `#include <iostream>

// ── 2D array — array of arrays ────────────────────────────────
int matrix[3][4] = {
    {1,  2,  3,  4},    // row 0
    {5,  6,  7,  8},    // row 1
    {9, 10, 11, 12}     // row 2
};

// ── Accessing 2D elements ─────────────────────────────────────
matrix[0][0];   // 1   — row 0, col 0
matrix[1][2];   // 7   — row 1, col 2
matrix[2][3];   // 12  — row 2, col 3 (last)

// ── Iterating 2D array ────────────────────────────────────────
int rows = 3, cols = 4;
for (int r = 0; r < rows; ++r) {
    for (int c = 0; c < cols; ++c)
        std::cout << matrix[r][c] << '\t';
    std::cout << '\n';
}

// ── Memory layout — ROW-MAJOR ─────────────────────────────────
// Elements stored in memory: [0][0] [0][1] [0][2] [0][3] [1][0]...
// Accessing row-by-row is cache-friendly!

// ── Passing 2D arrays to functions ───────────────────────────
// Must specify all dimensions EXCEPT the first:
void print2D(int arr[][4], int rows) {   // cols must be known!
    for (int r=0; r<rows; ++r)
        for (int c=0; c<4; ++c)
            std::cout << arr[r][c] << ' ';
}

// ── Prefer vector<vector<int>> for dynamic 2D arrays ──────────
#include <vector>
std::vector<std::vector<int>> grid(3, std::vector<int>(4, 0));
grid[1][2] = 99;  // no size constraints, fully dynamic`,
    },
    {
        label: "Common Algorithms",
        code: `#include <algorithm>
#include <iostream>

int arr[] = {5, 2, 8, 1, 9, 3, 7, 4, 6};
int n = 9;

// ── Sort ──────────────────────────────────────────────────────
std::sort(arr, arr + n);                      // ascending: 1 2 3 4 5 6 7 8 9
std::sort(arr, arr + n, std::greater<int>()); // descending: 9 8 7 6 5 4 3 2 1

// ── Search ────────────────────────────────────────────────────
// Linear: O(n)
int* found = std::find(arr, arr + n, 7);
if (found != arr + n) std::cout << "Found at index " << (found - arr);

// Binary (ARRAY MUST BE SORTED): O(log n)
std::sort(arr, arr + n);
bool exists = std::binary_search(arr, arr + n, 7);    // true
int* lb     = std::lower_bound(arr, arr + n, 7);      // first >= 7
int* ub     = std::upper_bound(arr, arr + n, 7);      // first > 7

// ── Reverse ───────────────────────────────────────────────────
std::reverse(arr, arr + n);

// ── Min / Max ─────────────────────────────────────────────────
int mn = *std::min_element(arr, arr + n);   // dereference the returned pointer
int mx = *std::max_element(arr, arr + n);
auto [lo, hi] = std::minmax_element(arr, arr + n); // C++17

// ── Fill ──────────────────────────────────────────────────────
std::fill(arr, arr + n, 0);     // all zeros
std::fill_n(arr, 5, 99);        // first 5 become 99`,
    },
];

const cArrayMethods = [
    { op: "arr[i]",                   complex: "O(1)",      desc: "Access element at index i. No bounds check — UB if out of range!" },
    { op: "sizeof(arr)/sizeof(arr[0])",complex: "O(1)",     desc: "Get number of elements. Only works in same scope where array is declared" },
    { op: "std::sort(arr, arr+n)",     complex: "O(n log n)",desc: "Sort ascending. Pass custom comparator as 3rd argument" },
    { op: "std::find(arr, arr+n, v)",  complex: "O(n)",     desc: "Linear search. Returns pointer past end if not found" },
    { op: "std::fill(arr, arr+n, v)",  complex: "O(n)",     desc: "Fill all elements with value v" },
    { op: "std::reverse(arr, arr+n)",  complex: "O(n)",     desc: "Reverse elements in-place" },
    { op: "std::copy(src,src+n,dst)",  complex: "O(n)",     desc: "Copy elements from source to destination range" },
    { op: "std::accumulate(arr,arr+n,0)",complex:"O(n)",    desc: "Sum all elements. Needs #include <numeric>" },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 2: std::array
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const stdArrayTabs = [
    {
        label: "Declaration & Methods",
        code: `#include <array>
#include <iostream>
#include <algorithm>

// ── std::array<T, N> — fixed-size, type-safe, stack-allocated ─
std::array<int, 5> a = {3, 1, 4, 1, 5};
std::array<int, 5> b{};                   // zero-initialised
std::array<std::string, 3> names = {"Alice", "Bob", "Carol"};

// ── Access ────────────────────────────────────────────────────
a[0];          // 3  — no bounds check (fast, like C-array)
a.at(0);       // 3  — bounds-checked, throws std::out_of_range
a.front();     // first element: 3
a.back();      // last element:  5
a.data();      // raw pointer to underlying array (for C API)

// ── Info ──────────────────────────────────────────────────────
a.size();      // 5 — constexpr, known at compile time
a.empty();     // false
constexpr size_t N = a.size();   // ← compile-time constant!

// ── Modify ───────────────────────────────────────────────────
a.fill(0);     // all zeros: {0,0,0,0,0}
a.swap(b);     // swap entire contents — O(N) (unlike vector swap)

// ── Iterate ───────────────────────────────────────────────────
for (auto& x : a) std::cout << x << ' ';
for (auto it = a.begin(); it != a.end(); ++it) std::cout << *it;

// ── STL algorithms ────────────────────────────────────────────
std::sort(a.begin(), a.end());              // {1,1,3,4,5}
std::reverse(a.begin(), a.end());           // {5,4,3,1,1}
auto it = std::find(a.begin(), a.end(), 4); // iterator to 4
std::get<0>(a);  // compile-time indexed access — bounds-checked at compile time!`,
    },
    {
        label: "vs C-Array vs vector",
        code: `#include <array>
#include <vector>

// ── Comparison: C-array vs std::array vs std::vector ──────────
//
//                  C-array   std::array   std::vector
//  Size            fixed     fixed        DYNAMIC
//  Memory          stack     stack        heap
//  Bounds check    ❌ none   ✅ at()      ✅ at()
//  Knows own size  ❌ no     ✅ .size()   ✅ .size()
//  Pass by value   ❌ decays ✅ copies    ✅ copies
//  STL compatible  ⚠️ partial ✅ full     ✅ full
//  Overhead        none      none         allocator

// ── When to use std::array ─────────────────────────────────────
// ✅ Size known at compile time (config arrays, lookup tables)
// ✅ Stack allocation (fast, cache-friendly)
// ✅ Passing arrays to functions without losing size info
// ✅ Return arrays from functions (C-arrays can't!)

std::array<int,3> makeRGB() { return {255, 128, 0}; }  // ✅ can return!
// int* makeRGB() { int arr[3] = {..}; return arr; }  // ❌ dangling ptr!

// ── Passing std::array keeps size info ───────────────────────
void process(std::array<int,5>& arr) {
    std::cout << arr.size();   // ✅ still 5 — not lost like C-array
}

// ── Template version for any size ────────────────────────────
template<size_t N>
void printAll(const std::array<int,N>& arr) {
    for (auto x : arr) std::cout << x << ' ';
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 3: std::vector as dynamic array
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const vectorArrayTabs = [
    {
        label: "Core Operations",
        code: `#include <vector>
#include <iostream>
#include <algorithm>
#include <numeric>

std::vector<int> v = {3, 1, 4, 1, 5, 9, 2, 6};

// ── Access ────────────────────────────────────────────────────
v[0];          // 3   — no bounds check
v.at(0);       // 3   — bounds-checked, throws out_of_range
v.front();     // 3   — first element
v.back();      // 6   — last element

// ── Modify ────────────────────────────────────────────────────
v.push_back(7);         // append 7
v.emplace_back(8);      // construct 8 in-place (avoid copy)
v.pop_back();           // remove last element (no return value!)
v.insert(v.begin()+2, 99);   // insert 99 at index 2
v.erase(v.begin());          // remove first element
v.erase(v.begin()+1, v.begin()+3); // erase range [1,3)
v.clear();              // remove ALL elements

// ── Size & Capacity ───────────────────────────────────────────
v.size();        // number of elements currently stored
v.capacity();    // allocated space (may be > size)
v.empty();       // true if size == 0
v.reserve(100);  // pre-allocate 100 slots — avoid reallocations
v.resize(10, 0); // change size to 10, fill new elements with 0
v.shrink_to_fit();  // release excess capacity

// ── STL algorithms ────────────────────────────────────────────
std::sort(v.begin(), v.end());
int sum = std::accumulate(v.begin(), v.end(), 0);
auto it = std::find(v.begin(), v.end(), 9);
std::reverse(v.begin(), v.end());`,
    },
    {
        label: "2D Vector",
        code: `#include <vector>
#include <iostream>

// ── Dynamic 2D "array" using vector<vector<T>> ────────────────
int rows = 3, cols = 4;
std::vector<std::vector<int>> grid(rows, std::vector<int>(cols, 0));
// grid is 3 rows × 4 cols, all zeros

// ── Access ────────────────────────────────────────────────────
grid[1][2] = 99;
grid.at(1).at(2);   // bounds-checked both dimensions

// ── Iterate ───────────────────────────────────────────────────
for (int r = 0; r < rows; ++r) {
    for (int c = 0; c < cols; ++c)
        std::cout << grid[r][c] << '\t';
    std::cout << '\n';
}
// Or range-for:
for (const auto& row : grid)
    for (int val : row)
        std::cout << val << ' ';

// ── Jagged 2D (rows of different lengths) ─────────────────────
std::vector<std::vector<int>> triangle;
for (int i = 1; i <= 5; ++i)
    triangle.push_back(std::vector<int>(i, i));  // row i has i copies of i

// ── Add/remove rows dynamically ───────────────────────────────
grid.push_back(std::vector<int>(cols, -1));   // add new row
grid.pop_back();                               // remove last row`,
    },
    {
        label: "Erase-Remove Idiom",
        code: `#include <vector>
#include <algorithm>

std::vector<int> v = {1, 2, 3, 2, 4, 2, 5};

// ── Erase-Remove idiom — remove all occurrences of a value ────
// std::remove doesn't actually erase — it moves them to the end
// and returns iterator to the new logical end
auto newEnd = std::remove(v.begin(), v.end(), 2);
// v is now: {1, 3, 4, 5, ?, ?, ?}  (? = unspecified)
v.erase(newEnd, v.end());   // actually remove the tail
// v = {1, 3, 4, 5}

// ── Remove-if with predicate ──────────────────────────────────
std::vector<int> nums = {1, 2, 3, 4, 5, 6, 7, 8};
auto it = std::remove_if(nums.begin(), nums.end(),
    [](int x){ return x % 2 == 0; });  // remove evens
nums.erase(it, nums.end());
// nums = {1, 3, 5, 7}

// ── C++20 shorthand: std::erase and std::erase_if ─────────────
std::vector<int> w = {1, 2, 3, 2, 4};
std::erase(w, 2);                                   // remove all 2s
std::erase_if(w, [](int x){ return x > 3; });       // remove if > 3

// ── Remove duplicates (sorted) ────────────────────────────────
std::vector<int> d = {1, 1, 2, 2, 3, 3};
std::sort(d.begin(), d.end());
d.erase(std::unique(d.begin(), d.end()), d.end());
// d = {1, 2, 3}`,
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 4: std::string
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const stringTabs = [
    {
        label: "Declaration & Basics",
        code: `#include <string>
#include <iostream>

// ── Creating strings ──────────────────────────────────────────
std::string s1;                    // empty string ""
std::string s2 = "Hello";          // from string literal
std::string s3("World");           // constructor syntax
std::string s4(5, '*');            // "  *****"  — 5 copies of '*'
std::string s5 = s2;               // copy — "Hello"
std::string s6 = s2 + ", " + s3;   // concatenation — "Hello, World"

// ── Access characters ─────────────────────────────────────────
s2[0];          // 'H'  — no bounds check
s2.at(0);       // 'H'  — bounds-checked (throws out_of_range)
s2.front();     // 'H'  — first char
s2.back();      // 'o'  — last char

// ── Iterate ───────────────────────────────────────────────────
for (char c : s2)  std::cout << c;         // range-for
for (int i = 0; i < (int)s2.size(); ++i)
    std::cout << s2[i];                     // index-based

// ── Info ──────────────────────────────────────────────────────
s2.size();      // 5  (same as length())
s2.length();    // 5
s2.empty();     // false
s2.capacity();  // allocated bytes (usually >= size)

// ── C-string interop ──────────────────────────────────────────
const char* cstr = s2.c_str();   // get null-terminated C string
std::string fromC = cstr;        // convert C string → std::string`,
    },
    {
        label: "Modification",
        code: `#include <string>
#include <algorithm>

std::string s = "Hello, World!";

// ── Append / Concatenate ──────────────────────────────────────
s += " How are you?";              // append in-place (efficient)
s.append(" Fine.");                // same as +=
s.append(3, '!');                  // append "!!!"
s.push_back('?');                  // append single character

// ── Insert ────────────────────────────────────────────────────
s.insert(5, " there");             // "Hello there, World!"
s.insert(s.begin(), 'X');          // insert char at iterator position

// ── Erase ────────────────────────────────────────────────────
s.erase(0, 6);                     // remove 6 chars starting at 0
s.erase(s.begin());                // remove first char
s.pop_back();                      // remove last char (no return value!)

// ── Replace ───────────────────────────────────────────────────
s.replace(0, 5, "Hi");             // replace 5 chars from index 0 with "Hi"

// ── Case conversion (no built-in — use transform) ─────────────
std::string upper = s;
std::transform(upper.begin(), upper.end(), upper.begin(), ::toupper);
std::string lower = s;
std::transform(lower.begin(), lower.end(), lower.begin(), ::tolower);

// ── Clear / Assign ────────────────────────────────────────────
s.clear();                         // s = ""
s.assign("New content");           // replace entire string
s.resize(3);                       // truncate to "New"
s.resize(8, '.');                  // pad to "New....."`,
    },
    {
        label: "Searching & Substrings",
        code: `#include <string>
#include <iostream>

std::string text = "The quick brown fox jumps over the lazy dog";

// ── find — returns index or std::string::npos if not found ────
size_t pos = text.find("fox");              // 16
size_t pos2 = text.find("cat");            // std::string::npos = -1 (max size_t)

if (text.find("fox") != std::string::npos)
    std::cout << "Found!";

// ── Find with start position ──────────────────────────────────
text.find("the", 5);               // search from index 5
text.rfind("the");                  // last occurrence (reverse find)
text.find_first_of("aeiou");        // index of first vowel
text.find_last_of("aeiou");         // index of last vowel
text.find_first_not_of("Thequick "); // first char NOT in that set

// ── Substring ─────────────────────────────────────────────────
std::string sub = text.substr(4, 5);       // "quick" — start=4, length=5
std::string tail = text.substr(16);        // "fox jumps..." — from 16 to end

// ── Count all occurrences ────────────────────────────────────
int count = 0;
size_t p = 0;
while ((p = text.find("the", p)) != std::string::npos) {
    ++count; ++p;
}

// ── Replace all occurrences ───────────────────────────────────
std::string replaceAll(std::string str, const std::string& from, const std::string& to) {
    size_t pos = 0;
    while ((pos = str.find(from, pos)) != std::string::npos) {
        str.replace(pos, from.size(), to);
        pos += to.size();
    }
    return str;
}`,
    },
    {
        label: "Comparison & Conversion",
        code: `#include <string>
#include <sstream>
#include <iostream>

// ── String comparison ─────────────────────────────────────────
std::string a = "apple", b = "banana";
a == b;          // false
a != b;          // true
a < b;           // true — lexicographic ('a' < 'b')
a.compare(b);    // negative: a comes before b
                 // 0: equal,  positive: a comes after b

// ── Number ↔ String conversion ───────────────────────────────
// int / double → string:
std::string s1 = std::to_string(42);       // "42"
std::string s2 = std::to_string(3.14);     // "3.140000"

// string → int / double:
int    n = std::stoi("123");               // 123
long   l = std::stol("9999999");           // long
double d = std::stod("3.14");             // 3.14
float  f = std::stof("2.71");             // float

// ── String streams — flexible parsing ────────────────────────
std::istringstream iss("10 20 30 40");
int x;
while (iss >> x) std::cout << x << ' ';   // 10 20 30 40

// ── Split string by delimiter ─────────────────────────────────
#include <vector>
std::vector<std::string> split(const std::string& s, char delim) {
    std::vector<std::string> tokens;
    std::istringstream ss(s);
    std::string token;
    while (std::getline(ss, token, delim))
        tokens.push_back(token);
    return tokens;
}
auto words = split("one,two,three", ',');  // {"one","two","three"}`,
    },
    {
        label: "string_view (C++17)",
        code: `#include <string_view>
#include <string>
#include <iostream>

// ── std::string_view — non-owning read-only view ──────────────
// Like const string& but lighter: just a pointer + length
// NO allocation, NO copying — perfect for read-only parameters

void printLength(std::string_view sv) {    // ← prefer over const string&
    std::cout << sv.size() << ": " << sv << '\\n';
}

// Can accept string literals WITHOUT creating std::string:
printLength("Hello");                      // no allocation
printLength(std::string("World"));         // views the string
printLength("ABCDE");                      // views literal directly

// ── string_view operations (subset of string) ─────────────────
std::string_view sv = "Hello, World!";
sv.size();           // 13
sv.substr(0, 5);     // "Hello"  — returns another string_view (no copy!)
sv.find("World");    // 7
sv.starts_with("Hello");  // true  (C++20)
sv.ends_with("!");        // true  (C++20)
sv[0];               // 'H'
sv.remove_prefix(7); // sv now views "World!"
sv.remove_suffix(1); // sv now views "World"

// ── When to use what ──────────────────────────────────────────
// std::string       — owns data; needs to store/modify
// const std::string& — function param when you have a string
// std::string_view  — function param for ANY string-like input (FASTEST)
// const char*       — C API interop only`,
    },
];

const stringMethods = [
    { method: "s.size() / s.length()",    cat: "Info",    desc: "Number of characters — O(1)",                    example: 's.size(); // 5 for "Hello"' },
    { method: "s.empty()",                cat: "Info",    desc: "True if string is empty — O(1)",                  example: 's.empty(); // true if s == ""' },
    { method: "s[i] / s.at(i)",           cat: "Access",  desc: "Character access. at() is bounds-checked",       example: 's[0]; s.at(0); // \'H\'' },
    { method: "s.front() / s.back()",     cat: "Access",  desc: "First / last character — O(1)",                  example: 's.front(); // first char' },
    { method: "s.substr(pos, len)",        cat: "Access",  desc: "Extract substring starting at pos — O(len)",    example: 's.substr(0,3); // first 3 chars' },
    { method: "s.find(str, pos=0)",        cat: "Search",  desc: "First occurrence index. Returns npos if absent", example: 's.find("lo"); // 3 in "Hello"' },
    { method: "s.rfind(str)",              cat: "Search",  desc: "Last occurrence — searches right to left",       example: 's.rfind("l"); // 3 in "Hello"' },
    { method: "s += other",               cat: "Modify",  desc: "Append in-place — efficient O(n) amortised",     example: 's += " World";' },
    { method: "s.append(str)",            cat: "Modify",  desc: "Append string or n copies of char",              example: 's.append(3,\'!\'); // "!!!"' },
    { method: "s.insert(pos, str)",        cat: "Modify",  desc: "Insert at position — O(n) shifts",              example: 's.insert(5," there");' },
    { method: "s.erase(pos, len)",         cat: "Modify",  desc: "Remove len chars from pos — O(n)",              example: 's.erase(0, 6); // remove 6 chars' },
    { method: "s.replace(pos,len,str)",    cat: "Modify",  desc: "Replace len chars from pos with str",           example: 's.replace(0,5,"Hi");' },
    { method: "s.clear()",                cat: "Modify",  desc: "Make string empty — size becomes 0",             example: 's.clear(); // s == ""' },
    { method: "s.compare(other)",          cat: "Compare", desc: "Lexicographic compare. 0=equal, <0=before, >0=after", example: 's.compare("hello"); // 0 if equal' },
    { method: "std::to_string(n)",         cat: "Convert", desc: "Number to string",                               example: 'std::to_string(42); // "42"' },
    { method: "std::stoi / stod / stof",   cat: "Convert", desc: "String to int / double / float",                example: 'std::stoi("123"); // 123' },
    { method: "s.c_str()",                cat: "Convert", desc: "Get const char* for C API compatibility",        example: 'printf("%s", s.c_str());' },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 5: C-style strings
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const cStringTabs = [
    {
        label: "char arrays & literals",
        code: `#include <cstring>   // C string functions
#include <iostream>

// ── C-style string = char array ending with null terminator ───
char str1[] = "Hello";       // {'H','e','l','l','o','\\0'} — 6 bytes
char str2[10] = "Hi";        // buffer with room for 9 chars + '\\0'
const char* str3 = "World";  // pointer to string literal (read-only!)

// ── str3[0] = 'X';  ← UNDEFINED BEHAVIOUR — literal is read-only ──
// Use char array for mutable C strings:
char mutable_str[] = "Hello";
mutable_str[0] = 'J';        // ✅ OK — array is mutable

// ── Null terminator is crucial ────────────────────────────────
char manual[6];
manual[0]='H'; manual[1]='i'; manual[2]='\\0';  // MUST null-terminate!
std::cout << manual;   // "Hi" — stops at \\0

// ── Length does NOT include null terminator ───────────────────
strlen("Hello");   // 5 (not 6)
sizeof("Hello");   // 6 (includes \\0)

// ── Passing C strings ─────────────────────────────────────────
void greet(const char* name) {    // const = won't modify
    std::cout << "Hello, " << name;
}
// Use std::string in modern C++ — avoid C strings when possible!`,
    },
    {
        label: "<cstring> Functions",
        code: `#include <cstring>
#include <iostream>

char dest[50];
const char* src = "Hello, World!";

// ── Copy ──────────────────────────────────────────────────────
strcpy(dest, src);             // ❌ UNSAFE — no bounds check!
strncpy(dest, src, 49);        // ✅ safer — max 49 chars + \\0
dest[49] = '\\0';               // always null-terminate after strncpy!

// ── Concatenate ───────────────────────────────────────────────
char buf[50] = "Hello";
strcat(buf, ", World!");       // ❌ UNSAFE — may overflow!
strncat(buf, " end", 10);      // ✅ safer — appends at most 10 chars

// ── Compare ───────────────────────────────────────────────────
int r = strcmp("apple", "banana");   // negative (a < b)
int r2 = strcmp("hello", "hello");   // 0 (equal)
strncmp("Hello", "HelloWorld", 5);   // 0 (first 5 chars equal)

// ── Length ────────────────────────────────────────────────────
strlen("Hello");     // 5 — does NOT count \\0

// ── Search ───────────────────────────────────────────────────
char* p = strchr("Hello", 'l');  // pointer to first 'l' (index 2)
char* q = strstr("Hello World", "World");  // pointer to "World"

// ── Memory functions (work on raw bytes) ──────────────────────
int data[5];
memset(data, 0, sizeof(data));              // zero all bytes
memcpy(dest, src, strlen(src)+1);           // copy bytes
memcmp("abc", "abd", 3);                    // byte comparison

// ── STRONGLY PREFER std::string in modern C++ ─────────────────
// C string functions are error-prone, unsafe, and harder to read`,
    },
];

// Summary comparison table
const typeComparison = [
    { type: "C-array int[]",      alloc: "Stack",  size: "Fixed", bounds: "❌ None",   stl: "⚠️ Partial", use: "Legacy code, tiny fixed buffers" },
    { type: "std::array<T,N>",    alloc: "Stack",  size: "Fixed", bounds: "✅ at()",   stl: "✅ Full",    use: "Fixed-size with STL and type safety" },
    { type: "std::vector<T>",     alloc: "Heap",   size: "Dynamic", bounds: "✅ at()", stl: "✅ Full",    use: "Default choice for arrays" },
    { type: "const char*",        alloc: "Static", size: "Fixed", bounds: "❌ None",   stl: "❌ No",      use: "String literals, C API params" },
    { type: "char[]",             alloc: "Stack",  size: "Fixed", bounds: "❌ None",   stl: "❌ No",      use: "C-style mutable char buffer (avoid)" },
    { type: "std::string",        alloc: "Heap*",  size: "Dynamic", bounds: "✅ at()", stl: "✅ Full",    use: "Default choice for text" },
    { type: "std::string_view",   alloc: "None",   size: "Fixed", bounds: "⚠️ Partial", stl: "✅ Full",  use: "Read-only string param (fastest)" },
];

// Cheatsheet blocks
const cheatBlocks = [
    {
        title: "Arrays", color: "#f0883e",
        items: [
            "✅ Prefer std::vector for dynamic arrays",
            "✅ Use std::array<T,N> for fixed-size on stack",
            "✅ Always pass size with C-arrays to functions",
            "✅ Use reserve() to avoid vector reallocations",
            "✅ Range-for works on all three types",
            "❌ Never access arr[n] — index is 0 to n-1",
            "❌ Don't return local C-array (dangling pointer!)",
            "❌ Don't use VLAs (int arr[n]) — non-standard C++",
        ],
    },
    {
        title: "Strings", color: "#58a6ff",
        items: [
            "✅ Use std::string for all text in modern C++",
            "✅ Use string_view for read-only string params",
            "✅ Use += for concatenation (not + in loops)",
            "✅ Always check find() result != string::npos",
            "✅ Use istringstream to split strings",
            "❌ Don't use strcpy/strcat — use std::string",
            "❌ Don't modify string literals (const char*)",
            "❌ Never forget null terminator in C strings",
        ],
    },
    {
        title: "Common Algorithms", color: "#7ee787",
        items: [
            "std::sort(v.begin(), v.end())",
            "std::find(v.begin(), v.end(), val)",
            "std::binary_search(v.begin(), v.end(), val)",
            "std::reverse(v.begin(), v.end())",
            "std::accumulate(v.begin(), v.end(), 0)",
            "std::count(v.begin(), v.end(), val)",
            "std::fill(v.begin(), v.end(), val)",
            "std::copy(src.begin(),src.end(),dst.begin())",
        ],
    },
    {
        title: "Common Mistakes", color: "#f85149",
        items: [
            "❌ Off-by-one: loop i <= n instead of i < n",
            "❌ Modifying container while iterating it",
            "❌ Using erase without checking empty()",
            "❌ Using find() result without npos check",
            "❌ Confusing strlen (no \\0) vs sizeof (has \\0)",
            "❌ Using push_back in tight loop — use reserve()",
            "❌ Returning reference to local string/array",
            "❌ Comparing C strings with == (use strcmp)",
        ],
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function CppArraysStrings() {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [cArrTab,  setCArrTab]  = useState(0);
    const [stdArrTab,setStdArrTab]= useState(0);
    const [vecTab,   setVecTab]   = useState(0);
    const [strTab,   setStrTab]   = useState(0);
    const [cStrTab,  setCStrTab]  = useState(0);
    const [methodFilter, setMethodFilter] = useState("All");

    const methodCategories = ["All", ...new Set(stringMethods.map(m => m.cat))];
    const visibleMethods = methodFilter === "All"
        ? stringMethods
        : stringMethods.filter(m => m.cat === methodFilter);

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{
                    fontWeight: 800, color: "goldenrod", display: "inline-block",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                    border: "1px solid rgba(218,165,32,0.25)", letterSpacing: "0.04em",
                }}>
                    C++ ARRAYS &amp; STRINGS
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                    C-arrays · std::array · std::vector · std::string · string_view · C-strings
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                    {["C-style Array","std::array","std::vector","std::string","string_view","<cstring>"].map(t => (
                        <Chip key={t} label={t} size="small" sx={{
                            bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.85)",
                            border: "1px solid rgba(218,165,32,0.25)", fontSize: "0.72rem",
                        }} />
                    ))}
                </Box>
            </Box>

            {/* ── Intro Banner ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ bgcolor: "rgba(218,165,32,0.06)", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "14px", p: "18px 24px", display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "1.4rem", flexShrink: 0 }}>💡</Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.85, color: "rgba(255,255,255,0.8)" }}>
                        C++ offers <strong style={{ color: "goldenrod" }}>three array types</strong> (C-array, std::array, std::vector)
                        and <strong style={{ color: "goldenrod" }}>three string types</strong> (const char*, std::string, std::string_view).
                        Choosing the right type for the job is one of the most impactful decisions you can make for
                        correctness, safety, and performance.
                    </Typography>
                </Box>
            </Box>

            {/* ── Type Decision Guide ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 2 }}>
                    {[
                        { icon:"📐", title:"Fixed size?",   q:"Known at compile time?", yes:"std::array<T,N>",  no:"std::vector<T>",    color:"#f0883e" },
                        { icon:"📝", title:"Text data?",    q:"Need to modify?",         yes:"std::string",      no:"std::string_view",  color:"#58a6ff" },
                        { icon:"⚡", title:"Performance?",  q:"Avoid heap allocation?",  yes:"Stack: array/str", no:"Heap: vector/string",color:"#7ee787" },
                    ].map((card, i) => (
                        <Box key={i} sx={{ bgcolor: "rgba(26,14,52,0.9)", border: `1px solid ${card.color}44`, borderTop: `3px solid ${card.color}`, borderRadius: "14px", p: "14px 16px" }}>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.75 }}>
                                <Typography sx={{ fontSize: "1.3rem" }}>{card.icon}</Typography>
                                <Typography sx={{ fontWeight: 800, color: card.color, fontSize: "0.85rem" }}>{card.title}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)", mb: 1 }}>{card.q}</Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography sx={{ fontSize: "0.78rem" }}>✅ Yes → <span style={{ color: card.color, fontFamily: "monospace", fontWeight: 700 }}>{card.yes}</span></Typography>
                                <Typography sx={{ fontSize: "0.78rem" }}>➡️ No  → <span style={{ color: "rgba(255,255,255,0.6)", fontFamily: "monospace" }}>{card.no}</span></Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════════
              SECTION 1 — C-STYLE ARRAYS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. C-Style Arrays
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            A <strong>C-style array</strong> is a fixed-size block of contiguous memory. It is
                            the fastest and most fundamental array in C++, but also the most dangerous — there
                            are no bounds checks and arrays decay to pointers when passed to functions.
                        </Typography>

                        <ConceptCard title="Memory Layout" color="#f0883e">
                            <CodeBlock>{`// int arr[5] = {10, 20, 30, 40, 50};
//
//  Index:    [0]   [1]   [2]   [3]   [4]
//  Value:     10    20    30    40    50
//  Address: 1000  1004  1008  1012  1016   (4 bytes per int)
//             ↑
//           arr (decays to pointer to this address)
//
//  arr[2]    → *(arr + 2) → value at address 1008 → 30
//  &arr[0]   → 1000  (address of first element)
//  arr + 1   → 1004  (pointer arithmetic: +1 = +sizeof(int) bytes)
//  arr[5]    → UNDEFINED BEHAVIOUR — reads past the array!`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={cArrTab} onChange={(_, v) => setCArrTab(v)} tabs={cArrayTabs.map(t => t.label)} />
                        <CodeBlock>{cArrayTabs[cArrTab].code}</CodeBlock>

                        <SectionHeading icon={<AiFillBulb size={18} />} title="C-Array Operations & Complexity" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Operation", "Complexity", "Notes"]} cols="2fr 100px 2fr" />}
                            {cArrayMethods.map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.82rem", mb: 0.4 }}>{row.op}</Typography>
                                        <PillChip label={row.complex} color={row.complex === "O(1)" ? "#56d364" : row.complex.includes("log") ? "#fbbf24" : "#f0883e"} />
                                        <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", mt: 0.5 }}>{row.desc}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "2fr 100px 2fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", color: "goldenrod", fontSize: "0.8rem", fontWeight: 600 }}>{row.op}</Box>
                                        <PillChip label={row.complex} color={row.complex === "O(1)" ? "#56d364" : row.complex.includes("log") ? "#fbbf24" : "#f0883e"} />
                                        <Box sx={{ fontSize: "0.81rem", opacity: 0.75 }}>{row.desc}</Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 2 — std::array
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. std::array — Fixed-Size Safe Array
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <code style={{ color: "goldenrod" }}>std::array&lt;T, N&gt;</code> is a thin wrapper
                            around a C-array that adds bounds checking, keeps size information, supports all STL
                            algorithms, and can be returned from functions — with zero runtime overhead.
                        </Typography>

                        <StyledTabs value={stdArrTab} onChange={(_, v) => setStdArrTab(v)} tabs={stdArrayTabs.map(t => t.label)} />
                        <CodeBlock>{stdArrayTabs[stdArrTab].code}</CodeBlock>

                        {/* std::array advantages grid */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🏆</span>} title="Why Choose std::array Over C-Array?" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 2 }}>
                            {[
                                { icon: "🔒", color: "#56d364", title: "Bounds Safety",    body: 'at() throws std::out_of_range. std::get<I>() gives compile-time bounds check.' },
                                { icon: "📏", color: "#58a6ff", title: "Knows Its Size",   body: 'size() is always correct. No sizeof(arr)/sizeof(arr[0]) hack needed.' },
                                { icon: "📦", color: "#f0883e", title: "Returnable",       body: "Can be returned from functions. C-array can't — you'd return a dangling pointer!" },
                                { icon: "🔗", color: "#a855f7", title: "STL Compatible",   body: "Works with std::sort, std::find, range-for — full iterator support." },
                                { icon: "⚡", color: "#fbbf24", title: "Zero Overhead",    body: "Stack-allocated. Same performance as C-array. Compiler optimises it away." },
                                { icon: "📋", color: "#e879f9", title: "Copyable",         body: "std::array copies all elements by value. C-array can't be copied by assignment." },
                            ].map((b, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${b.color}33`, borderRadius: "12px", p: "14px 16px" }}>
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                                        <Typography sx={{ fontSize: "1.1rem" }}>{b.icon}</Typography>
                                        <Typography sx={{ fontWeight: 700, color: b.color, fontSize: "0.82rem" }}>{b.title}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "0.8rem", opacity: 0.72, lineHeight: 1.7 }}>{b.body}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 3 — std::vector
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            3. std::vector — Dynamic Array
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <code style={{ color: "goldenrod" }}>std::vector&lt;T&gt;</code> is the most-used
                            container in C++. It stores elements in contiguous heap memory and grows
                            automatically. Use it as the default array type unless you have a specific reason
                            to use std::array.
                        </Typography>

                        <ConceptCard title="Capacity vs Size" color="#7ee787">
                            <CodeBlock>{`// std::vector maintains two counts:
//
//  size     = number of elements currently stored
//  capacity = total memory currently allocated
//
//  Initial: size=0, capacity=0
//  push_back(1): size=1, capacity=1
//  push_back(2): size=2, capacity=2
//  push_back(3): size=3, capacity=4  ← doubled! (reallocated)
//  push_back(4): size=4, capacity=4
//  push_back(5): size=5, capacity=8  ← doubled again!
//
//  ── WHY? ─────────────────────────────────────────────────────
//  Reallocation = allocate new block + copy all elements + free old
//  Doubling strategy means O(1) amortised push_back
//  Use reserve(n) if you know the final size — avoids all reallocs!
//
//  v.reserve(100);  // allocate once upfront
//  for (int i=0; i<100; ++i) v.push_back(i);  // no reallocations!`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={vecTab} onChange={(_, v) => setVecTab(v)} tabs={vectorArrayTabs.map(t => t.label)} />
                        <CodeBlock>{vectorArrayTabs[vecTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 4 — std::string
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            4. std::string — The Modern C++ String
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <code style={{ color: "goldenrod" }}>std::string</code> is a dynamic, heap-managed
                            sequence of characters. It handles all the memory concerns of C-strings automatically
                            and provides a rich API for searching, modifying, and converting text.
                        </Typography>

                        <ConceptCard title="SSO — Small String Optimisation" color="#58a6ff">
                            <CodeBlock>{`// Most std::string implementations use SSO (Small String Optimisation):
//
//  Short strings (≤ ~15 chars): stored INSIDE the string object (stack!)
//  Long strings  (> ~15 chars): allocated on the HEAP
//
//  std::string s = "Hi";        // stored in-object — no heap alloc!
//  std::string s = "This is a longer string..."; // heap allocated
//
//  This means short string operations are extremely fast.
//  The exact threshold depends on the implementation (GCC, Clang, MSVC).
//
//  ── Practical implication ────────────────────────────────────
//  Don't avoid std::string for short strings out of performance fear.
//  SSO makes it nearly as fast as a stack char[] for short text.`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={strTab} onChange={(_, v) => setStrTab(v)} tabs={stringTabs.map(t => t.label)} />
                        <CodeBlock>{stringTabs[strTab].code}</CodeBlock>

                        {/* String methods reference */}
                        <SectionHeading icon={<AiFillBulb size={18} />} title="std::string Methods Reference" />
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            {methodCategories.map(cat => (
                                <Chip key={cat} label={cat} onClick={() => setMethodFilter(cat)} variant={methodFilter === cat ? "filled" : "outlined"} size="small"
                                      sx={{ cursor: "pointer", fontSize: "0.72rem", fontWeight: 600, color: methodFilter === cat ? "#100720" : "rgba(255,255,255,0.55)", bgcolor: methodFilter === cat ? "goldenrod" : "transparent", borderColor: methodFilter === cat ? "goldenrod" : "rgba(255,255,255,0.2)", "&:hover": { borderColor: "goldenrod", color: "goldenrod" } }} />
                            ))}
                        </Box>
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Method", "Category", "Description", "Example"]} cols="1.5fr 80px 1.5fr 1.5fr" />}
                            {visibleMethods.map((row, i) => {
                                const catStyles = { Info: "#a855f7", Access: "#58a6ff", Modify: "#f0883e", Search: "#7ee787", Compare: "#fbbf24", Convert: "#e879f9" };
                                const c = catStyles[row.cat] || "goldenrod";
                                return isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Box sx={{ display: "flex", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                                            <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.8rem" }}>{row.method}</Typography>
                                            <PillChip label={row.cat} color={c} />
                                        </Box>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", mb: 0.4 }}>{row.desc}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.75rem" }}>{row.example}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1.5fr 80px 1.5fr 1.5fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", color: "goldenrod", fontWeight: 600, fontSize: "0.79rem" }}>{row.method}</Box>
                                        <PillChip label={row.cat} color={c} />
                                        <Box sx={{ fontSize: "0.8rem", opacity: 0.73 }}>{row.desc}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{row.example}</Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 5 — C-STYLE STRINGS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            5. C-Style Strings &amp; &lt;cstring&gt;
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            A <strong>C-style string</strong> is a null-terminated <code style={{ color: "goldenrod" }}>char</code> array.
                            You'll encounter them in C APIs, legacy code, and string literals. Know how they work,
                            but <strong>prefer std::string in new code</strong>.
                        </Typography>

                        <ConceptCard title="The Null Terminator '\\0'" color="#f85149">
                            <CodeBlock>{`// char str[] = "Hello";
//
//  Memory:  ['H'] ['e'] ['l'] ['l'] ['o'] ['\\0']
//  Index:    [0]   [1]   [2]   [3]   [4]   [5]
//
//  '\\0' is ASCII 0 — the sentinel that marks the end of the string
//  strlen counts UNTIL '\\0' → 5  (not including \\0)
//  sizeof counts THE WHOLE ARRAY  → 6 (includes \\0)
//
//  If you forget to null-terminate: functions read garbage until
//  they hit a 0 byte somewhere in memory → buffer over-read!
//
//  char bad[5] = {'H','e','l','l','o'};  // NO null terminator!
//  strlen(bad);  ← reads past array boundary → UNDEFINED BEHAVIOUR`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={cStrTab} onChange={(_, v) => setCStrTab(v)} tabs={cStringTabs.map(t => t.label)} />
                        <CodeBlock>{cStringTabs[cStrTab].code}</CodeBlock>

                        {/* cstring safe alternatives */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>⚠️</span>} title="Unsafe C Functions vs Safe Alternatives" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Unsafe (Avoid)", "Safe Alternative", "Reason"]} />}
                            {[
                                { bad: "strcpy(dst, src)",     good: "strncpy(dst,src,n) + dst[n]='\\0'",  reason: "No bounds check — can overflow dst buffer" },
                                { bad: "strcat(dst, src)",     good: "strncat(dst, src, n)",                reason: "No bounds check — appends past buffer end" },
                                { bad: "gets(buf)",            good: "fgets(buf, n, stdin)",                reason: "No size limit — classic buffer overflow" },
                                { bad: "sprintf(buf, ...)",    good: "snprintf(buf, n, ...)",               reason: "No bounds check on output buffer" },
                                { bad: "strcmp(a, b) == 0",    good: "std::string a == b",                  reason: "Use std::string, or remember strcmp result" },
                                { bad: "char* as string",      good: "std::string",                          reason: "Manual memory, null-term bugs, unsafe functions" },
                            ].map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontFamily: "monospace", color: "#f85149", fontSize: "0.8rem", mb: 0.4 }}>❌ {row.bad}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#56d364", fontSize: "0.8rem", mb: 0.4 }}>✅ {row.good}</Typography>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)" }}>{row.reason}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", fontSize: "0.82rem", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", color: "#f85149", fontSize: "0.79rem" }}>❌ {row.bad}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#56d364", fontSize: "0.79rem" }}>✅ {row.good}</Box>
                                        <Box sx={{ opacity: 0.65 }}>{row.reason}</Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              TYPE COMPARISON & CHEATSHEET
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3}>
                            Type Comparison &amp; Cheatsheet
                        </Typography>

                        {/* Full comparison table */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>📊</span>} title="All Types Side by Side" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)", mb: 4 }}>
                            {!isMobile && <TableHeader columns={["Type", "Allocation", "Size", "Bounds Check", "STL", "Best For"]} cols="1.2fr 70px 75px 100px 75px 1.5fr" />}
                            {typeComparison.map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.88rem", mb: 0.5 }}>{row.type}</Typography>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", mb: 0.4 }}>{row.alloc} · {row.size} · Bounds: {row.bounds}</Typography>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.7)" }}>{row.use}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1.2fr 70px 75px 100px 75px 1.5fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", fontSize: "0.82rem", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.8rem" }}>{row.type}</Box>
                                        <Box sx={{ opacity: 0.65, fontSize: "0.79rem" }}>{row.alloc}</Box>
                                        <Box sx={{ opacity: 0.65, fontSize: "0.79rem" }}>{row.size}</Box>
                                        <Box sx={{ fontSize: "0.79rem" }}>{row.bounds}</Box>
                                        <Box sx={{ fontSize: "0.79rem" }}>{row.stl}</Box>
                                        <Box sx={{ opacity: 0.75, fontSize: "0.79rem" }}>{row.use}</Box>
                                    </Box>
                                )
                            )}
                        </Box>

                        {/* Four cheat blocks */}
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 3 }}>
                            {cheatBlocks.map((block, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${block.color}33`, borderRadius: "14px", p: "18px 20px" }}>
                                    <Typography sx={{ fontWeight: 800, color: block.color, fontSize: "0.88rem", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{block.title}</Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                                        {block.items.map((item, j) => (
                                            <Typography key={j} sx={{ fontSize: "0.8rem", opacity: 0.82, lineHeight: 1.65, fontFamily: item.startsWith("std::") ? "monospace" : "inherit", color: item.startsWith("std::") ? "#a5d6ff" : "inherit" }}>{item}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
